/**
 * Agent 注册表 Store
 *
 * 生产级数据源管理：
 *  1. 初始化时使用前端常量（REGISTERED_AGENTS）确保首屏即刻渲染
 *  2. 异步从后端 Agent 目录 API 加载最新数据（API 优先）
 *  3. API 不可用时记录 loadError；生产入口不得把前端常量当真实接口数据展示
 *
 * 所有需要 Agent 元数据的组件/composable 应从本 Store 消费，
 * 禁止直接引用 constants/agents.ts 中的 BUSINESS_AGENTS 作为运行时数据源。
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { agentsApi, type Agent } from '@/api/agents'
import { REGISTERED_AGENTS, type AgentRegistryItem, type MaturityLevel } from '@/constants/agents'
import type { AgentId } from '@/types/agent-types'
import { getErrorMessage } from '@/utils/get-error-message'
import { logger } from '@/utils/logger'

/** 前端常量按 id 索引，供 DB 主导模式取 icon/color/文案/路由兜底 */
const _BUILTIN_BY_ID = new Map(REGISTERED_AGENTS.map(a => [a.id, a]))

/**
 * DB 主导合并（PR6 / Phase 4）：以 API（DB active 记录）为权威生成注册表。
 * 前端常量仅提供 icon/color/文案/路由/分类兜底；DB 未返回的 Agent 不出现，
 * 即 DB inactive/deprecated 的 Agent 不会被前端常量复活。
 */
export function mergeDbAuthority(apiAgents: Agent[]): AgentRegistryItem[] {
  return apiAgents.map(api => {
    const builtin = _BUILTIN_BY_ID.get(api.id as AgentId)
    return {
      ...(builtin ?? {}),
      // 治理口径：DB 权威
      id: api.id as AgentId,
      business: api.business === true,
      surfaceType: api.surface_type,
      listVisible: api.list_visible,
      enabled: true, // 已按 status=active 过滤
      // 展示字段：API 优先，前端常量兜底
      label: api.name?.trim() || builtin?.label || api.id,
      role: api.role || builtin?.role,
      icon: api.icon || builtin?.icon || '🤖',
      color: api.color || builtin?.color || '#6366f1',
      avatarUrl: api.avatar_url || builtin?.avatarUrl,
      description: api.description || builtin?.description || '',
      category: builtin?.category ?? 'utility',
      routePath: builtin?.routePath ?? `/agents/workspace?agent=${api.id}`,
    }
  })
}

// TODO: [useApiQuery 迁移] fetchFromApi 的 loading/try/catch/finally 模板代码可用 useApiQuery 声明式替换
//   此 store 合并逻辑较复杂（API + 前端常量骨架合并），暂不迁移
export const useAgentRegistryStore = defineStore('agentRegistry', () => {
  /** 是否已从后端 API 加载过 */
  const loaded = ref(false)
  /** 是否正在加载中 */
  const loading = ref(false)
  /** 最近一次 Agent 目录接口加载错误；生产 UI 用它阻止静态兜底误展示。 */
  const loadError = ref('')
  /** 复用进行中的加载 Promise，避免并发重复请求 */
  let _loadPromise: Promise<void> | null = null

  /** Agent 注册表（内部可写），初始值为前端常量 */
  // REGISTERED_AGENTS 是 as const 冻结对象，展开时显式复制控制字段以确保 reactive 可追踪
  const _registry = ref<AgentRegistryItem[]>(
    REGISTERED_AGENTS.map(a => ({ ...a, enabled: a.enabled, business: a.business })),
  )
  /** Agent 注册表（只读视图，防止外部意外修改） */
  const registry = computed<readonly AgentRegistryItem[]>(() => _registry.value)

  /**
   * 显示模式：
   * - 'production'（默认）：仅展示 maturityLevel >= L4 的业务 Agent
   * - 'full'：忽略成熟度限制，展示全部业务 Agent（用于开发调试）
   */
  const displayMode = import.meta.env.VITE_AGENT_DISPLAY_MODE || 'production'

  /**
   * DB 主导模式（PR6 / Phase 4，单向门）：
   * - 关闭（默认）：沿用「前端常量为骨架、API 覆盖展示字段」的旧策略。
   * - 开启：以 DB（API）为权威决定业务 Agent 范围与可见性，前端常量只提供
   *   icon/color/文案/路由兜底；DB inactive/deprecated 的 Agent 不被常量复活。
   * 必须在 PR4 数据迁移上线、线上口径验证为 13 业务后才可翻开此 flag。
   */
  const dbAuthority = import.meta.env.VITE_AGENT_DB_AUTHORITY === 'true'

  /** 判断成熟度等级是否达到 L4+（生产就绪阈值） */
  function isProductionReady(level: MaturityLevel | undefined): boolean {
    if (!level) return false
    // L0 < L1 < L2 < L3 < L4 < L5，字符串比较在此字母序下等价于数值比较
    return level >= 'L4'
  }

  /** 业务 Agent（根据 displayMode / dbAuthority 决定过滤策略） */
  const businessAgents = computed<AgentRegistryItem[]>(() => {
    // DB 主导模式：可见性完全由 DB 决定——business=true 且 list_visible 未被显式置否。
    // 不再用前端 maturityLevel 门槛（成熟度由后端治理口径承载）。
    if (dbAuthority) {
      return _registry.value.filter(a => a.business && a.listVisible !== false)
    }
    if (displayMode === 'full') {
      return _registry.value.filter(a => a.business)
    }
    // 生产模式：只展示 maturityLevel >= L4 且 enabled === true 的 Agent
    return _registry.value.filter(
      a => a.business && a.enabled === true && isProductionReady(a.maturityLevel),
    )
  })

  /** Agent ID → 元数据 快速查找 */
  const agentById = computed<Record<string, AgentRegistryItem>>(() =>
    Object.fromEntries(_registry.value.map(a => [a.id, a])),
  )

  /** 所有已注册的 Agent ID 集合 */
  const agentIdSet = computed<Set<string>>(() =>
    new Set(_registry.value.map(a => a.id)),
  )

  /**
   * 从 Agent 目录 API 加载最新数据并合并到注册表。
   *
   * 合并策略：
   *  - 以 REGISTERED_AGENTS 为基准骨架（决定哪些是业务 Agent、路由路径等）
   *  - API 返回的同 ID 记录仅覆盖展示字段（label/icon/color/description）
   *  - API 中多出来的记录（Skill、子 Agent、未上线 Agent）不纳入业务 Agent 范畴
   */
  async function fetchFromApi(): Promise<void> {
    if (loading.value) return
    loading.value = true
    loadError.value = ''
    try {
      // DB 主导模式：只请求 active，DB 为权威
      if (dbAuthority) {
        const response = await agentsApi.listAgents({ status: 'active' })
        _registry.value = mergeDbAuthority(response.agents)
        loaded.value = true
        return
      }

      const response = await agentsApi.listAgents()
      const apiMap = new Map(response.agents.map(a => [a.id, a]))

      // 以前端注册表为骨架，用 API 数据覆盖展示字段
      // API label（品牌名）优先，前端常量作为降级兜底
      const merged: AgentRegistryItem[] = REGISTERED_AGENTS.map(builtin => {
        const base = {
          ...builtin,
          enabled: builtin.enabled,
          business: builtin.business,
        }
        const apiAgent = apiMap.get(builtin.id)
        if (!apiAgent) return base
        return {
          ...base,
          label: apiAgent.name?.trim() || builtin.label,    // API 优先，加 trim() 防御
          role: apiAgent.role || builtin.role,
          icon: apiAgent.icon || builtin.icon,
          color: apiAgent.color || builtin.color,
          avatarUrl: apiAgent.avatar_url || builtin.avatarUrl,
          description: apiAgent.description || builtin.description,
          // 以下字段仍从前端常量读取（后端数据不准确）
          category: builtin.category,
        }
      })

      _registry.value = merged
      loaded.value = true
    } catch (error) {
      loaded.value = false
      loadError.value = getErrorMessage(error, '智能体目录接口不可用')
      logger.warn('[AgentRegistry] API 加载失败，生产入口不展示前端常量兜底:', error)
    } finally {
      loading.value = false
    }
  }

  /**
   * 确保注册表已从 API 加载。
   * 并发安全：多个调用方共享同一个 Promise，全部等待同一次 API 请求完成。
   */
  async function ensureLoaded(): Promise<void> {
    if (loaded.value) return
    if (!_loadPromise) {
      _loadPromise = fetchFromApi().finally(() => { _loadPromise = null })
    }
    return _loadPromise
  }

  return {
    loaded,
    loading,
    loadError,
    registry,
    businessAgents,
    agentById,
    agentIdSet,
    fetchFromApi,
    ensureLoaded,
  }
})
